import React, { useState, useEffect } from 'react';
import ImageWithBasePath from '../../core/img/imagewithbasebath';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ChevronUp, Filter, Sliders, Zap } from 'react-feather';
import { setToogleHeader } from '../../core/redux/action';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { RotateCcw } from 'feather-icons-react/build/IconComponents';
//import { DatePicker } from 'antd';
import { toast } from 'react-toastify';
import { roleAPI } from '../../core/action/api';
import userActions from '../../core/action/userAction';

const Permissions = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [rolePermissions, setRolePermissions] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    //const [selectedDate, setSelectedDate] = useState(null);
    const [saving, setSaving] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState(null);

    const toggleFilterVisibility = () => {
        setIsFilterVisible((prevVisibility) => !prevVisibility);
    };

    // Load roles on component mount
    useEffect(() => {
        loadRoles();
        loadAllPermissions();
    }, []);

    // Load roles from API
    const loadRoles = async () => {
        setLoading(true);
        try {
            const response = await userActions.getRoles();
            if (response.success) {
                setRoles(response.data);
                if (response.data.length > 0 && !selectedRole) {
                    setSelectedRole(response.data[0]);
                    setSelectedRoleId(response.data[0].id);
                    loadRolePermissions(response.data[0].id);
                }
            } else {
                toast.error(response.message || 'Failed to load roles');
            }
        } catch (error) {
            console.error('Error loading roles:', error);
            toast.error('Failed to load roles');
        } finally {
            setLoading(false);
        }
    };

    // Load all permissions
    const loadAllPermissions = async () => {
        try {
            const response = await userActions.getPermissions();
            if (response.success) {
                setPermissions(response.flat || []);
            } else {
                console.error('Failed to load permissions:', response.message);
            }
        } catch (error) {
            console.error('Error loading permissions:', error);
        }
    };

    // Load permissions for a specific role
    const loadRolePermissions = async (roleId) => {
        try {
            const response = await userActions.getRolePermissions(roleId);
            if (response.success) {
                const permSet = new Set(response.data);
                setRolePermissions(prev => ({
                    ...prev,
                    [roleId]: permSet
                }));
            } else {
                console.error('Failed to load role permissions:', response.message);
            }
        } catch (error) {
            console.error('Error loading role permissions:', error);
        }
    };

    // Handle role selection change
    const handleRoleChange = (selected) => {
        const role = roles.find(r => r.id === selected.value);
        setSelectedRole(role);
        setSelectedRoleId(role.id);
        if (!rolePermissions[role.id]) {
            loadRolePermissions(role.id);
        }
    };

    // Toggle permission for a role
    const togglePermission = async (moduleId, permissionSlug, isChecked) => {
        if (!selectedRoleId) {
            toast.error('Please select a role first');
            return;
        }

        setSaving(true);
        try {
            // Find permission ID from slug
            const permission = permissions.find(p => p.permission_slug === permissionSlug);
            if (!permission) {
                toast.error('Permission not found');
                return;
            }

            let updatedPermissions = new Set(rolePermissions[selectedRoleId] || []);
            
            if (isChecked) {
                updatedPermissions.add(permission.id);
            } else {
                updatedPermissions.delete(permission.id);
            }

            // Prepare data for API
            const permissionIds = Array.from(updatedPermissions);
            
            // Save to API
            const response = await roleAPI.updatePermissions(selectedRoleId, permissionIds);
            
            if (response.success) {
                setRolePermissions(prev => ({
                    ...prev,
                    [selectedRoleId]: updatedPermissions
                }));
                toast.success(`Permission ${isChecked ? 'granted' : 'revoked'} successfully`);
            } else {
                toast.error(response.message || 'Failed to update permissions');
            }
        } catch (error) {
            console.error('Error updating permissions:', error);
            toast.error('Failed to update permissions');
        } finally {
            setSaving(false);
        }
    };

    // Toggle all permissions for a module
    const toggleModulePermissions = async (moduleName, modulePermissions, isChecked) => {
        if (!selectedRoleId) {
            toast.error('Please select a role first');
            return;
        }

        setSaving(true);
        try {
            const currentPerms = new Set(rolePermissions[selectedRoleId] || []);
            
            modulePermissions.forEach(perm => {
                if (isChecked) {
                    currentPerms.add(perm.id);
                } else {
                    currentPerms.delete(perm.id);
                }
            });

            const permissionIds = Array.from(currentPerms);
            const response = await roleAPI.updatePermissions(selectedRoleId, permissionIds);
            
            if (response.success) {
                setRolePermissions(prev => ({
                    ...prev,
                    [selectedRoleId]: currentPerms
                }));
                toast.success(`${moduleName} permissions ${isChecked ? 'granted' : 'revoked'} successfully`);
            } else {
                toast.error(response.message || 'Failed to update permissions');
            }
        } catch (error) {
            console.error('Error updating module permissions:', error);
            toast.error('Failed to update permissions');
        } finally {
            setSaving(false);
        }
    };

    // Check if role has a specific permission
    const hasPermission = (permissionSlug) => {
        if (!selectedRoleId || !rolePermissions[selectedRoleId]) return false;
        const permission = permissions.find(p => p.permission_slug === permissionSlug);
        if (!permission) return false;
        return rolePermissions[selectedRoleId].has(permission.id);
    };

    // Group permissions by module
    const groupPermissionsByModule = () => {
        const grouped = {};
        permissions.forEach(permission => {
            const module = permission.module || 'General';
            if (!grouped[module]) {
                grouped[module] = [];
            }
            grouped[module].push(permission);
        });
        return grouped;
    };

    // Filter permissions by search term
    const getFilteredPermissions = () => {
        const grouped = groupPermissionsByModule();
        if (!searchTerm) return grouped;
        
        const filtered = {};
        Object.keys(grouped).forEach(module => {
            const filteredPermissions = grouped[module].filter(perm => 
                perm.permission_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                perm.permission_slug.toLowerCase().includes(searchTerm.toLowerCase())
            );
            if (filteredPermissions.length > 0) {
                filtered[module] = filteredPermissions;
            }
        });
        return filtered;
    };

    // Check if all permissions in module are selected
    // const isModuleFullySelected = (modulePermissions) => {
    //     if (!selectedRoleId || !rolePermissions[selectedRoleId]) return false;
    //     return modulePermissions.every(perm => rolePermissions[selectedRoleId].has(perm.id));
    // };

    // Check if any permission in module is selected
    const isModulePartiallySelected = (modulePermissions) => {
        if (!selectedRoleId || !rolePermissions[selectedRoleId]) return false;
        const anySelected = modulePermissions.some(perm => rolePermissions[selectedRoleId].has(perm.id));
        const allSelected = modulePermissions.every(perm => rolePermissions[selectedRoleId].has(perm.id));
        return anySelected && !allSelected;
    };

    const roleOptions = roles.map(role => ({
        value: role.id,
        label: role.role_name
    }));

    const groupedPermissions = getFilteredPermissions();

    // Handle refresh
    const handleRefresh = async () => {
        if (selectedRoleId) {
            await loadRolePermissions(selectedRoleId);
            toast.info('Permissions refreshed');
        } else {
            await loadRoles();
            toast.info('Roles refreshed');
        }
    };

    return (
        <div>
            <div className="page-wrapper">
                <div className="content">
                    <div className="page-header">
                        <div className="add-item d-flex">
                            <div className="page-title">
                                <h4>Role & Permission Management</h4>
                                <h6>Manage role permissions</h6>
                            </div>
                        </div>
                        <ul className="table-top-head">
                            <li>
                                <OverlayTrigger placement="top" overlay={<Tooltip>Refresh</Tooltip>}>
                                    <Link onClick={handleRefresh}>
                                        <RotateCcw />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                            <li>
                                <OverlayTrigger placement="top" overlay={<Tooltip>Collapse</Tooltip>}>
                                    <Link
                                        id="collapse-header"
                                        className={data ? "active" : ""}
                                        onClick={() => { dispatch(setToogleHeader(!data)) }}
                                    >
                                        <ChevronUp />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                        </ul>
                    </div>

                    <div className="card table-list-card">
                        <div className="card-body">
                            <div className="table-top">
                                <div className="search-set">
                                    <div className="search-input">
                                        <input
                                            type="text"
                                            placeholder="Search permissions..."
                                            className="form-control form-control-sm formsearch"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <Link className="btn btn-searchset">
                                            <i data-feather="search" className="feather-search" />
                                        </Link>
                                    </div>
                                </div>
                                <div className="search-path">
                                    <Link className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`} id="filter_search">
                                        <Filter
                                            className="filter-icon"
                                            onClick={toggleFilterVisibility}
                                        />
                                        <span onClick={toggleFilterVisibility}>
                                            <ImageWithBasePath src="assets/img/icons/closes.svg" alt="img" />
                                        </span>
                                    </Link>
                                </div>
                                <div className="form-sort">
                                    <Sliders className="info-img" />
                                    <Select
                                        className="select"
                                        options={roleOptions}
                                        value={selectedRole ? { value: selectedRole.id, label: selectedRole.role_name } : null}
                                        onChange={handleRoleChange}
                                        placeholder="Select Role"
                                        isDisabled={loading}
                                    />
                                </div>
                            </div>

                            {/* Filter Section */}
                            <div
                                className={`card${isFilterVisible ? ' visible' : ''}`}
                                id="filter_inputs"
                                style={{ display: isFilterVisible ? 'block' : 'none' }}
                            >
                                <div className="card-body pb-0">
                                    <div className="row">
                                        <div className="col-lg-3 col-sm-6 col-12">
                                            <div className="input-blocks">
                                                <Zap className="info-img" />
                                                <Select
                                                    className="select"
                                                    options={roleOptions}
                                                    value={selectedRole ? { value: selectedRole.id, label: selectedRole.role_name } : null}
                                                    onChange={handleRoleChange}
                                                    placeholder="Choose Role"
                                                    isDisabled={loading}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-lg-3 col-sm-6 col-12 ms-auto">
                                            <div className="input-blocks">
                                                <button 
                                                    className="btn btn-filters ms-auto"
                                                    onClick={() => setSearchTerm('')}
                                                >
                                                    <i data-feather="x" className="feather-search" /> Clear
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading permissions...</p>
                                    </div>
                                ) : !selectedRole ? (
                                    <div className="text-center py-5">
                                        <p className="text-muted">Please select a role to view and manage permissions</p>
                                    </div>
                                ) : Object.keys(groupedPermissions).length === 0 ? (
                                    <div className="text-center py-5">
                                        <p className="text-muted">No permissions found</p>
                                    </div>
                                ) : (
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th className="no-sort" style={{ width: '40px' }}>
                                                    <label className="checkboxs">
                                                        <input 
                                                            type="checkbox" 
                                                            id="select-all"
                                                            onChange={() => {
                                                                const allPermissions = Object.values(groupedPermissions).flat();
                                                                const allSelected = allPermissions.every(p => hasPermission(p.permission_slug));
                                                                allPermissions.forEach(p => {
                                                                    togglePermission(p.id, p.permission_slug, !allSelected);
                                                                });
                                                            }}
                                                            checked={Object.values(groupedPermissions).flat().every(p => hasPermission(p.permission_slug))}
                                                            disabled={saving}
                                                        />
                                                        <span className="checkmarks" />
                                                    </label>
                                                </th>
                                                <th>Modules</th>
                                                <th>Create</th>
                                                <th>Edit</th>
                                                <th>Delete</th>
                                                <th>View</th>
                                                <th className="no-sort">Allow all</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(groupedPermissions).map(([module, modulePermissions]) => {
                                                const createPerm = modulePermissions.find(p => p.permission_slug.includes('create'));
                                                const editPerm = modulePermissions.find(p => p.permission_slug.includes('edit'));
                                                const deletePerm = modulePermissions.find(p => p.permission_slug.includes('delete'));
                                                const viewPerm = modulePermissions.find(p => p.permission_slug.includes('view'));
                                                
                                                const allSelected = modulePermissions.every(p => hasPermission(p.permission_slug));
                                                const partSelected = isModulePartiallySelected(modulePermissions);
                                                
                                                return (
                                                    <tr key={module}>
                                                        <td>
                                                            <label className="checkboxs">
                                                                <input 
                                                                    type="checkbox"
                                                                    checked={allSelected}
                                                                    ref={input => {
                                                                        if (input) {
                                                                            input.indeterminate = partSelected && !allSelected;
                                                                        }
                                                                    }}
                                                                    onChange={(e) => toggleModulePermissions(module, modulePermissions, e.target.checked)}
                                                                    disabled={saving}
                                                                />
                                                                <span className="checkmarks" />
                                                            </label>
                                                        </td>
                                                        <td>{module}</td>
                                                        <td>
                                                            {createPerm && (
                                                                <label className="checkboxs">
                                                                    <input 
                                                                        type="checkbox"
                                                                        checked={hasPermission(createPerm.permission_slug)}
                                                                        onChange={(e) => togglePermission(createPerm.id, createPerm.permission_slug, e.target.checked)}
                                                                        disabled={saving}
                                                                    />
                                                                    <span className="checkmarks" />
                                                                </label>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {editPerm && (
                                                                <label className="checkboxs">
                                                                    <input 
                                                                        type="checkbox"
                                                                        checked={hasPermission(editPerm.permission_slug)}
                                                                        onChange={(e) => togglePermission(editPerm.id, editPerm.permission_slug, e.target.checked)}
                                                                        disabled={saving}
                                                                    />
                                                                    <span className="checkmarks" />
                                                                </label>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {deletePerm && (
                                                                <label className="checkboxs">
                                                                    <input 
                                                                        type="checkbox"
                                                                        checked={hasPermission(deletePerm.permission_slug)}
                                                                        onChange={(e) => togglePermission(deletePerm.id, deletePerm.permission_slug, e.target.checked)}
                                                                        disabled={saving}
                                                                    />
                                                                    <span className="checkmarks" />
                                                                </label>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {viewPerm && (
                                                                <label className="checkboxs">
                                                                    <input 
                                                                        type="checkbox"
                                                                        checked={hasPermission(viewPerm.permission_slug)}
                                                                        onChange={(e) => togglePermission(viewPerm.id, viewPerm.permission_slug, e.target.checked)}
                                                                        disabled={saving}
                                                                    />
                                                                    <span className="checkmarks" />
                                                                </label>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <label className="checkboxs">
                                                                <input 
                                                                    type="checkbox"
                                                                    checked={allSelected}
                                                                    onChange={(e) => toggleModulePermissions(module, modulePermissions, e.target.checked)}
                                                                    disabled={saving}
                                                                />
                                                                <span className="checkmarks" />
                                                            </label>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                            
                            {saving && (
                                <div className="text-center py-3">
                                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                                    <span>Saving permissions...</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Permissions;